/**
 * Shared primitives for the LifeAdvice admin section.
 *
 * Kept deliberately small: a page header (h4 700 + subtitle + HelpTip, matching
 * the Quill pages), an error-message normalizer, a category colour swatch, and
 * a write-error handler that turns the advice service's 403
 * INSUFFICIENT_PRIVILEGES into a plain-English snackbar instead of an error
 * page — view_only admins can browse every page here, they just can't save.
 */

import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { HelpTip } from '../../help/HelpTip';
import { LifeAdviceApiError } from '../../../services/lifeAdviceAdminService';

// ── Errors ────────────────────────────────────────────────────────────────

/** Normalize anything thrown by the service client into display copy. */
export function errorMessage(e: unknown, fallback: string): string {
  if (e instanceof LifeAdviceApiError) return e.message;
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

/**
 * Copy for a failed write. A 403 is not a fault — it's the view_only role
 * doing its job — so it gets its own explanatory sentence.
 */
export function writeErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof LifeAdviceApiError && e.status === 403) {
    return 'Insufficient privileges: your admin role is read-only for LifeAdvice content. Ask a super admin to make this change.';
  }
  return errorMessage(e, fallback);
}

/** True when the failure means "the API is not reachable", not "the API said no". */
export function isBackendDown(e: unknown): boolean {
  return (
    e instanceof LifeAdviceApiError &&
    (e.status === 0 || e.status === 502 || e.status === 503 || e.status === 504)
  );
}

// ── Page header ───────────────────────────────────────────────────────────

export interface PageHeaderProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  /** help-content.json tooltip id (see src/data/help-content.json). */
  helpId?: string;
  /** Right-aligned actions (refresh, create, export…). */
  actions?: React.ReactNode;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  icon,
  title,
  subtitle,
  helpId,
  actions,
}) => (
  <Box sx={{ mb: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
      {icon}
      <Typography variant="h4" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {helpId && <HelpTip id={helpId} />}
      <Box sx={{ flexGrow: 1 }} />
      {actions}
    </Box>
    <Typography variant="body1" color="text.secondary">
      {subtitle}
    </Typography>
  </Box>
);

// ── Category colour swatch ────────────────────────────────────────────────

export interface ColorSwatchProps {
  /** #RRGGBB from the API. */
  color?: string | null;
  /** Optional lighter companion colour (light_bg) shown as a second chip. */
  lightBg?: string | null;
  label?: string;
}

/** Renders the API's stored hex values as real swatches, with the hex as text. */
export const ColorSwatch: React.FC<ColorSwatchProps> = ({ color, lightBg, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <Tooltip title={label ? `${label} — ${color || 'no colour'}` : color || 'no colour'}>
      <Box
        aria-label={`colour ${color || 'unset'}`}
        sx={{
          width: 18,
          height: 18,
          borderRadius: '4px',
          flexShrink: 0,
          border: '1px solid rgba(0,0,0,0.15)',
          backgroundColor: color || 'transparent',
        }}
      />
    </Tooltip>
    {lightBg && (
      <Tooltip title={`Light background — ${lightBg}`}>
        <Box
          aria-label={`light background ${lightBg}`}
          sx={{
            width: 18,
            height: 18,
            borderRadius: '4px',
            flexShrink: 0,
            border: '1px solid rgba(0,0,0,0.15)',
            backgroundColor: lightBg,
          }}
        />
      </Tooltip>
    )}
    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
      {color || '—'}
    </Typography>
  </Box>
);

// ── Small formatters ──────────────────────────────────────────────────────

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

/** Words counted the same way the advice service's countWords() does. */
export function countWords(text: string): number {
  const matches = text.trim().match(/\S+/g);
  return matches ? matches.length : 0;
}
